import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { 
  Server, Code, ShoppingCart, Cpu, 
  CheckCircle, ArrowRight
} from 'lucide-react';

const SERVICE_IMAGES = {
  maintenance: "https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGJsdWUlMjBsaWdodHxlbnwwfHx8fDE3NzE0MzA5NTN8MA&ixlib=rb-4.1.0&q=85",
  software: "https://images.unsplash.com/photo-1607971422532-73f9d45d7a47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGNvZGluZyUyMHNjcmVlbnxlbnwwfHx8fDE3NzE0MzA5NTR8MA&ixlib=rb-4.1.0&q=85",
  hardware: "https://images.unsplash.com/photo-1680992044138-ce4864c2b962?w=800",
  iot: "https://images.unsplash.com/photo-1592919347498-69564e361791?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxJb1QlMjBzbWFydCUyMGRldmljZSUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzcxNDMwOTU1fDA&ixlib=rb-4.1.0&q=85",
};

export default function Services() {
  const { t } = useLanguage();

  const services = [
    {
      key: 'maintenance',
      icon: Server,
      color: 'from-cyan-500 to-blue-600',
    },
    {
      key: 'software',
      icon: Code,
      color: 'from-emerald-500 to-teal-600',
    },
    {
      key: 'hardware',
      icon: ShoppingCart,
      color: 'from-violet-500 to-purple-600',
    },
    {
      key: 'iot',
      icon: Cpu,
      color: 'from-orange-500 to-red-600',
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="services-page" className="circuit-bg">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-primary font-medium mb-2 block">{t('services.title')}</span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">{t('services.subtitle')}</h1>
          </motion.div>
        </div>
      </section>

      {/* Services Grid - Bento Style */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  data-testid={`service-detail-${service.key}`}
                  className="group h-full border border-border hover:border-primary/50 transition-all duration-300 overflow-hidden"
                >
                  {/* Service Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={SERVICE_IMAGES[service.key]}
                      alt={t(`services.items.${service.key}.title`)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                    <div className={`absolute bottom-4 left-4 w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                      <service.icon className="h-7 w-7 text-white" />
                    </div>
                  </div>

                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl">
                      {t(`services.items.${service.key}.title`)}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      {t(`services.items.${service.key}.desc`)}
                    </p>

                    {/* Features List */}
                    <ul className="space-y-2">
                      {t(`services.items.${service.key}.features`).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/contact">
                      <Button 
                        variant="outline"
                        className="w-full rounded-full mt-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                        data-testid={`service-cta-${service.key}`}
                      >
                        {t('services.cta')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('contact.subtitle')}
            </h2>
            <Link to="/contact">
              <Button 
                size="lg"
                data-testid="services-cta-main"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 glow-cyan glow-cyan-hover"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
